import axios from 'axios';
import {
    AUTH_REFRESH,
    USER_LOGIN,
} from '../constants/routes';
import localStorageService from './localStorageService';

type StoredAuthUser = Record<string, any> | null;
type JwtPayload = {
    exp?: number;
};

/**
 * Default per-request timeout for authenticatedFetch. Browser `fetch` has no
 * built-in timeout, so a stalled request would otherwise hang forever (and any
 * `finally` waiting on it never runs). Callers with legitimately long requests
 * (e.g. large uploads) can override via `timeoutMs`, or pass `timeoutMs: 0` /
 * their own `signal` to opt out.
 */
const DEFAULT_FETCH_TIMEOUT_MS = 30000;

type AuthFetchInit = RequestInit & { timeoutMs?: number };

/**
 * LoginAuthService
 * Contains the auth methods required for login, refresh,
 * access-token verification, and authenticated requests.
 */
class AuthService {
    /** Authenticate a user with username + password (form-encoded). */
    public UserLogin = async (payload: URLSearchParams) => {
        try {
            const response = await axios.post(USER_LOGIN, payload, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    };

    /** Refresh an expired access token using a refresh token. */
    public RefreshAuth = async (refreshToken: string) => {
        try {
            const response = await axios.post(
                AUTH_REFRESH,
                { refresh_token: refreshToken },
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    };

    public clearSession = () => {
        localStorageService.clearAll();
    };

    public getStoredUser = <T = StoredAuthUser>() => {
        return localStorageService.getUserInfo<T>();
    };

    public getAccessToken = (userData?: StoredAuthUser) => {
        const storedUser = userData ?? this.getStoredUser<StoredAuthUser>();

        if (!storedUser || typeof storedUser !== 'object') {
            return '';
        }

        return storedUser.access_token || storedUser.accessToken || '';
    };

    public getRefreshToken = (userData?: StoredAuthUser) => {
        const storedUser = userData ?? this.getStoredUser<StoredAuthUser>();

        if (!storedUser || typeof storedUser !== 'object') {
            return '';
        }

        return storedUser.refresh_token || storedUser.refreshToken || '';
    };

    public VerifyAuth = async () => {
        const storedUser = this.getStoredUser<StoredAuthUser>();

        if (!storedUser || typeof storedUser !== 'object') {
            this.clearSession();
            return false;
        }

        const accessToken = this.getAccessToken(storedUser);

        if (!accessToken) {
            this.clearSession();
            return false;
        }

        if (!this.isTokenExpired(accessToken)) {
            return true;
        }

        return this.refreshStoredAuth();
    };

    public authenticatedFetch = async (input: RequestInfo | URL, init: AuthFetchInit = {}) => {
        const isAuthenticated = await this.VerifyAuth();

        if (!isAuthenticated) {
            throw new Error('Authentication required. Please login again.');
        }

        const accessToken = this.getAccessToken();

        if (!accessToken) {
            this.clearSession();
            throw new Error('Authentication required. Please login again.');
        }

        const { timeoutMs = DEFAULT_FETCH_TIMEOUT_MS, ...fetchInit } = init;

        // Abort a stalled request after `timeoutMs` so the caller's promise
        // settles (rejects) instead of hanging forever. Skipped when the caller
        // supplies its own signal or sets timeoutMs to 0.
        const executeRequest = async (token: string) => {
            const useOwnTimeout = timeoutMs > 0 && !fetchInit.signal;
            const controller = useOwnTimeout ? new AbortController() : null;
            const timeoutId = controller
                ? setTimeout(() => controller.abort(), timeoutMs)
                : null;
            try {
                return await fetch(input, {
                    ...fetchInit,
                    headers: this.buildAuthorizedHeaders(fetchInit.headers, token),
                    signal: fetchInit.signal ?? controller?.signal,
                });
            } catch (err) {
                if (controller?.signal.aborted) {
                    throw new Error(
                        `Request timed out after ${Math.round(timeoutMs / 1000)}s. Please try again.`,
                    );
                }
                throw err;
            } finally {
                if (timeoutId) clearTimeout(timeoutId);
            }
        };

        let response = await executeRequest(accessToken);

        if (response.status !== 401) {
            return response;
        }

        const refreshSucceeded = await this.refreshStoredAuth();

        if (!refreshSucceeded) {
            throw new Error('Session expired. Please login again.');
        }

        const refreshedAccessToken = this.getAccessToken();

        if (!refreshedAccessToken) {
            this.clearSession();
            throw new Error('Session expired. Please login again.');
        }

        response = await executeRequest(refreshedAccessToken);

        if (response.status === 401) {
            this.clearSession();
            throw new Error('Session expired. Please login again.');
        }

        return response;
    };

    private buildAuthorizedHeaders = (headers: HeadersInit | undefined, accessToken: string) => {
        const nextHeaders = new Headers(headers);
        nextHeaders.set('Authorization', `Bearer ${accessToken}`);
        return nextHeaders;
    };

    private refreshStoredAuth = async () => {
        const storedUser = this.getStoredUser<StoredAuthUser>();
        const refreshToken = this.getRefreshToken(storedUser);

        if (!storedUser || typeof storedUser !== 'object' || !refreshToken) {
            this.clearSession();
            return false;
        }

        const response = await this.RefreshAuth(refreshToken);

        if (!response || response.status !== 200 || !response.data) {
            this.clearSession();
            return false;
        }

        const updatedUser = {
            ...storedUser,
            ...response.data,
        };
        const nextAccessToken = this.getAccessToken(updatedUser);

        if (!nextAccessToken) {
            this.clearSession();
            return false;
        }

        localStorageService.addUserInfo(updatedUser);
        return true;
    };

    private isTokenExpired = (token: string) => {
        const payload = this.decodeJwtPayload(token);

        if (!payload?.exp) {
            return false;
        }

        const expiresAtMs = payload.exp * 1000;
        const expiryBufferMs = 30 * 1000;

        return Date.now() >= expiresAtMs - expiryBufferMs;
    };

    private decodeJwtPayload = (token: string): JwtPayload | null => {
        const tokenParts = token.split('.');

        if (tokenParts.length !== 3) {
            return null;
        }

        const encodedPayload = tokenParts[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        const requiredPadding = encodedPayload.length % 4;
        const normalizedPayload = `${encodedPayload}${requiredPadding ? '='.repeat(4 - requiredPadding) : ''}`;

        try {
            if (typeof window === 'undefined' || typeof window.atob !== 'function') {
                return null;
            }

            return JSON.parse(window.atob(normalizedPayload)) as JwtPayload;
        } catch {
            return null;
        }
    };
}

export default AuthService;
